/**
 * Profile Page - Enterprise Grade
 * 
 * Comprehensive user profile management with security, billing, and activity tracking.
 * Features: Profile editing, 2FA, session management, usage analytics, and activity logs.
 */

import { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  User,
  Shield,
  Calendar,
  Camera,
  Save,
  Key,
  Bell,
  Globe,
  Palette,
  CreditCard,
  Activity,
  Lock,
  Smartphone,
  Monitor,
  MapPin,
  Trash2,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Eye,
  EyeOff,
  Download,
  RefreshCw,
  Mail,
  Building,
  Briefcase,
  Phone,
  Copy,
  Check,
  LogOut,
  Cpu,
  DollarSign,
  Zap,
  TrendingUp,
  BarChart3,
  PieChart,
  History,
  FileText,
  ExternalLink,
  Loader2,
  ChevronLeft,
  ChevronRight,
  Settings,
  Volume2,
  VolumeX,
  Webhook,
} from 'lucide-react';
import { useAuthStore } from '../stores/auth';
import { usePreferencesStore, type Timezone, type DateFormat } from '../stores/preferences';
import { Link, useSearchParams } from 'react-router-dom';
import { gpuJobsApi, activityApi, apiKeysApi, profileApi, twoFactorApi, sessionsApi, ActivityLog, UserSession, LoginHistoryEntry, NotificationPreferences } from '../lib/api';
import { formatDistanceToNow, format } from 'date-fns';
import toast from 'react-hot-toast';
import clsx from 'clsx';

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.08 },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 16 },
  visible: { opacity: 1, y: 0 },
};

// Toggle Switch Component
interface ToggleSwitchProps {
  enabled: boolean;
  onChange: (enabled: boolean) => void;
  disabled?: boolean;
  size?: 'sm' | 'md';
}

function ToggleSwitch({ enabled, onChange, disabled = false, size = 'md' }: ToggleSwitchProps) {
  const sizes = {
    sm: { track: 'w-9 h-5', thumb: 'w-3.5 h-3.5', translate: 'translate-x-4' },
    md: { track: 'w-12 h-6', thumb: 'w-4 h-4', translate: 'translate-x-6' },
  };
  const s = sizes[size];

  return (
    <button
      type="button"
      role="switch"
      aria-checked={enabled}
      disabled={disabled}
      onClick={() => !disabled && onChange(!enabled)}
      className={clsx(
        'relative inline-flex flex-shrink-0 rounded-full transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-gpu-500 focus:ring-offset-2 focus:ring-offset-surface-900',
        s.track,
        enabled ? 'bg-gpu-500' : 'bg-surface-700',
        disabled && 'opacity-50 cursor-not-allowed'
      )}
    >
      <span
        className={clsx(
          'pointer-events-none inline-block rounded-full bg-white shadow-lg transform ring-0 transition-transform duration-200 ease-in-out',
          s.thumb,
          enabled ? s.translate : 'translate-x-1',
          'mt-1'
        )}
      />
    </button>
  );
}

// Tab types
type TabType = 'profile' | 'security' | 'billing' | 'activity' | 'preferences' | 'notifications';

const ACTIVITY_PAGE_SIZE = 5;

export default function ProfilePage() {
  const { user } = useAuthStore();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Initialize active tab from URL parameter
  const tabFromUrl = searchParams.get('tab') as TabType | null;
  const validTabs: TabType[] = ['profile', 'security', 'billing', 'activity', 'preferences', 'notifications'];
  const [activeTab, setActiveTab] = useState<TabType>(tabFromUrl && validTabs.includes(tabFromUrl) ? tabFromUrl : 'profile');
  const [copiedText, setCopiedText] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  // Update active tab when URL parameter changes
  useEffect(() => {
    const tabFromUrl = searchParams.get('tab') as TabType | null;
    if (tabFromUrl && validTabs.includes(tabFromUrl)) {
      setActiveTab(tabFromUrl);
    }
  }, [searchParams, validTabs]);
  
  // Update URL when tab changes
  const handleTabChange = (tab: TabType) => {
    setActiveTab(tab);
    setSearchParams({ tab });
  };

  // Get preferences store
  const preferencesStore = usePreferencesStore();

  // Profile form state
  const [profileForm, setProfileForm] = useState({
    displayName: user?.email?.split('@')[0] || '',
    email: user?.email || '',
    company: '',
    jobTitle: '',
    phone: '',
    bio: '',
    timezone: preferencesStore.timezone,
    dateFormat: preferencesStore.dateFormat,
  });

  // Security state
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [twoFactorEnabled, setTwoFactorEnabled] = useState(false);
  const [showTwoFactorSetup, setShowTwoFactorSetup] = useState(false);
  const [twoFactorSetupData, setTwoFactorSetupData] = useState<{ secret: string; qrCodeUrl: string; recoveryCodes: string[] } | null>(null);
  const [twoFactorCode, setTwoFactorCode] = useState('');

  // Preference toggles - synced with store
  const [preferences, setPreferences] = useState({
    theme: preferencesStore.theme as 'dark' | 'light' | 'system',
    compactMode: preferencesStore.compactMode,
    jobCompletionAlerts: true,
    costAlerts: true,
    securityAlerts: true,
    productUpdates: false,
    weeklyDigest: true,
  });

  // Notifications state
  const [notificationPreferences, setNotificationPreferences] = useState<NotificationPreferences>({
    jobCompletionAlerts: true,
    costAlerts: true,
    securityAlerts: true,
    productUpdates: false,
    weeklyDigest: true,
    costAlertThreshold: 100,
  });

  // Additional notification settings not in the API
  const [additionalNotificationSettings, setAdditionalNotificationSettings] = useState({
    emailEnabled: true,
    emailDigestFrequency: 'weekly' as 'daily' | 'weekly' | 'monthly',
    pushEnabled: false,
    jobStartedAlerts: true,
    jobFailedAlerts: true,
    spotInterruptionAlerts: true,
    apiKeyUsageAlerts: false,
    webhookEnabled: false,
    webhookUrl: '',
    quietHoursEnabled: false,
    quietHoursStart: '22:00',
    quietHoursEnd: '08:00',
  });

  const [notificationHasChanges, setNotificationHasChanges] = useState(false);
  const [initialNotificationPreferences, setInitialNotificationPreferences] = useState<NotificationPreferences | null>(null);

  // Initialize preferences on mount
  useEffect(() => {
    preferencesStore.initializePreferences();
  }, []);

  // Avatar state
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null);
  const [isUploadingAvatar, setIsUploadingAvatar] = useState(false);

  // Activity pagination state
  const [activityPage, setActivityPage] = useState(0);

  // Login history pagination state
  const [loginHistoryPage, setLoginHistoryPage] = useState(0);
  const LOGIN_HISTORY_PAGE_SIZE = 5;

  // =====================
  // API Queries
  // =====================

  // Fetch user profile from API
  const { data: profileData } = useQuery({
    queryKey: ['user-profile'],
    queryFn: profileApi.getProfile,
    staleTime: 30000,
  });

  // Fetch notification preferences
  const { data: notificationPrefs } = useQuery({
    queryKey: ['notification-preferences'],
    queryFn: profileApi.getNotificationPreferences,
    staleTime: 30000,
  });

  // Fetch 2FA status
  const { data: twoFactorStatus } = useQuery({
    queryKey: ['2fa-status'],
    queryFn: twoFactorApi.getStatus,
    staleTime: 30000,
  });

  // Update local state when profile data is fetched
  useEffect(() => {
    if (profileData) {
      const timezone = (profileData.timezone || preferencesStore.timezone || 'utc') as Timezone;
      const dateFormat = (profileData.dateFormat || preferencesStore.dateFormat || 'mdy') as DateFormat;
      const theme = (profileData.theme || preferencesStore.theme || 'dark') as 'dark' | 'light' | 'system';
      const compactMode = profileData.compactMode ?? preferencesStore.compactMode ?? false;

      setProfileForm({
        displayName: profileData.displayName || user?.email?.split('@')[0] || '',
        email: profileData.email || user?.email || '',
        company: profileData.company || '',
        jobTitle: profileData.jobTitle || '',
        phone: profileData.phone || '',
        bio: profileData.bio || '',
        timezone,
        dateFormat,
      });
      if (profileData.avatarUrl) {
        setAvatarPreview(profileData.avatarUrl);
      }
      setPreferences(prev => ({
        ...prev,
        theme,
        compactMode,
      }));

      // Sync with preferences store
      preferencesStore.setTheme(theme);
      preferencesStore.setCompactMode(compactMode);
      preferencesStore.setTimezone(timezone);
      preferencesStore.setDateFormat(dateFormat);
    }
  }, [profileData, user?.email]);

  // Update notification preferences state
  useEffect(() => {
    if (notificationPrefs) {
      setNotificationPreferences(notificationPrefs);
      setInitialNotificationPreferences(notificationPrefs);
      setPreferences(prev => ({
        ...prev,
        jobCompletionAlerts: notificationPrefs.jobCompletionAlerts ?? true,
        costAlerts: notificationPrefs.costAlerts ?? true,
        securityAlerts: notificationPrefs.securityAlerts ?? true,
        productUpdates: notificationPrefs.productUpdates ?? false,
        weeklyDigest: notificationPrefs.weeklyDigest ?? true,
      }));
    }
  }, [notificationPrefs]);

  // Check for notification changes
  useEffect(() => {
    if (initialNotificationPreferences) {
      const prefsChanged = JSON.stringify(notificationPreferences) !== JSON.stringify(initialNotificationPreferences);
      setNotificationHasChanges(prefsChanged);
    }
  }, [notificationPreferences, initialNotificationPreferences]);

  // Update 2FA state
  useEffect(() => {
    if (twoFactorStatus) {
      setTwoFactorEnabled(twoFactorStatus.enabled);
    }
  }, [twoFactorStatus]);

  // Fetch jobs for stats
  const { data: jobs = [] } = useQuery({
    queryKey: ['gpu-jobs'],
    queryFn: gpuJobsApi.list,
  });

  // Fetch API keys for stats
  const { data: apiKeys = [] } = useQuery({
    queryKey: ['api-keys'],
    queryFn: apiKeysApi.list,
  });

  // Fetch user activity with pagination
  const { data: activityResponse, isLoading: activityLoading } = useQuery({
    queryKey: ['user-activity', activityPage],
    queryFn: () => activityApi.getRecent(ACTIVITY_PAGE_SIZE, activityPage * ACTIVITY_PAGE_SIZE),
  });

  const activityLogs = activityResponse?.data || [];
  const activityPagination = activityResponse?.pagination;
  const totalActivityPages = activityPagination ? Math.ceil(activityPagination.total / ACTIVITY_PAGE_SIZE) : 0;
  const hasNextPage = activityPagination?.hasMore ?? false;
  const hasPrevPage = activityPage > 0;

  // Fetch active sessions
  const { data: sessions = [], isLoading: sessionsLoading } = useQuery({
    queryKey: ['user-sessions'],
    queryFn: sessionsApi.list,
    staleTime: 30000,
  });

  // Fetch login history with pagination
  const { data: loginHistoryResponse, isLoading: loginHistoryLoading } = useQuery({
    queryKey: ['login-history', loginHistoryPage],
    queryFn: () => sessionsApi.getLoginHistory(LOGIN_HISTORY_PAGE_SIZE, loginHistoryPage * LOGIN_HISTORY_PAGE_SIZE),
    staleTime: 30000,
  });

  const loginHistory = loginHistoryResponse?.data || [];
  const loginHistoryTotal = loginHistoryResponse?.total || 0;
  const totalLoginHistoryPages = Math.ceil(loginHistoryTotal / LOGIN_HISTORY_PAGE_SIZE);
  const hasNextLoginHistoryPage = loginHistoryPage < totalLoginHistoryPages - 1;
  const hasPrevLoginHistoryPage = loginHistoryPage > 0;

  // =====================
  // API Mutations
  // =====================

  // Update profile mutation
  const updateProfileMutation = useMutation({
    mutationFn: profileApi.updateProfile,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user-profile'] });
      queryClient.invalidateQueries({ queryKey: ['user-activity'] });
      toast.success('Profile updated successfully');
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to update profile');
    },
  });

  // Change password mutation
  const changePasswordMutation = useMutation({
    mutationFn: profileApi.changePassword,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user-activity'] });
      toast.success('Password changed successfully');
      setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to change password');
    },
  });

  // Update preferences mutation
  const updatePreferencesMutation = useMutation({
    mutationFn: profileApi.updateNotificationPreferences,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notification-preferences'] });
      queryClient.invalidateQueries({ queryKey: ['user-profile'] });
      queryClient.invalidateQueries({ queryKey: ['user-activity'] });
      toast.success('Preferences saved successfully');
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to save preferences');
    },
  });

  // Export data mutation
  const exportDataMutation = useMutation({
    mutationFn: profileApi.exportData,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user-activity'] });
      toast.success('Your data export has been initiated. You will receive an email shortly.');
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to export data');
    },
  });

  // 2FA setup mutation
  const init2FAMutation = useMutation({
    mutationFn: twoFactorApi.initSetup,
    onSuccess: (data) => {
      setTwoFactorSetupData(data);
      setShowTwoFactorSetup(true);
      queryClient.invalidateQueries({ queryKey: ['user-activity'] });
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to initialize 2FA setup');
    },
  });

  // 2FA verify mutation
  const verify2FAMutation = useMutation({
    mutationFn: (code: string) => twoFactorApi.verifyAndEnable(code),
    onSuccess: () => {
      setTwoFactorEnabled(true);
      setShowTwoFactorSetup(false);
      setTwoFactorCode('');
      queryClient.invalidateQueries({ queryKey: ['2fa-status'] });
      queryClient.invalidateQueries({ queryKey: ['user-activity'] });
      toast.success('Two-factor authentication enabled');
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to verify 2FA code');
    },
  });

  // 2FA disable mutation
  const disable2FAMutation = useMutation({
    mutationFn: ({ password, code }: { password: string; code: string }) =>
      twoFactorApi.disable(password, code),
    onSuccess: () => {
      setTwoFactorEnabled(false);
      queryClient.invalidateQueries({ queryKey: ['2fa-status'] });
      queryClient.invalidateQueries({ queryKey: ['user-activity'] });
      toast.success('Two-factor authentication disabled');
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to disable 2FA');
    },
  });

  // Session revoke mutation
  const revokeSessionMutation = useMutation({
    mutationFn: sessionsApi.revoke,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user-sessions'] });
      queryClient.invalidateQueries({ queryKey: ['user-activity'] });
      toast.success('Session revoked successfully');
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to revoke session');
    },
  });

  // Revoke all sessions mutation
  const revokeAllSessionsMutation = useMutation({
    mutationFn: sessionsApi.revokeAll,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['user-sessions'] });
      queryClient.invalidateQueries({ queryKey: ['user-activity'] });
      toast.success(`All other sessions have been revoked (${data.revokedCount} sessions)`);
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to revoke sessions');
    },
  });

  // Delete account mutation
  const deleteAccountMutation = useMutation({
    mutationFn: profileApi.deleteAccount,
    onSuccess: () => {
      toast.success('Account deleted successfully');
      // Redirect to login or home
      window.location.href = '/login';
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to delete account');
    },
  });

  // Upload avatar mutation
  const uploadAvatarMutation = useMutation({
    mutationFn: profileApi.uploadAvatar,
    onSuccess: (data) => {
      setAvatarPreview(data.avatarUrl);
      queryClient.invalidateQueries({ queryKey: ['user-profile'] });
      queryClient.invalidateQueries({ queryKey: ['user-activity'] });
      toast.success('Profile photo uploaded successfully');
      setIsUploadingAvatar(false);
    },
    onError: (error: Error) => {
      setIsUploadingAvatar(false);
      toast.error(error.message || 'Failed to upload profile photo');
    },
  });

  // Regenerate recovery codes mutation
  const regenerateRecoveryCodesMutation = useMutation({
    mutationFn: twoFactorApi.regenerateRecoveryCodes,
    onSuccess: (data) => {
      // Download recovery codes as a text file
      const codesText = `Tyaus Recovery Codes\nGenerated: ${new Date().toISOString()}\n\nKeep these codes in a safe place. Each code can only be used once.\n\n${data.recoveryCodes.join('\n')}`;
      const blob = new Blob([codesText], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'tyaus-recovery-codes.txt';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      queryClient.invalidateQueries({ queryKey: ['user-activity'] });
      toast.success('New recovery codes generated and downloaded');
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to regenerate recovery codes');
    },
  });

  // Calculate real stats
  const totalJobs = jobs.length;
  const totalSpend = jobs.reduce((acc, j) => acc + j.estimatedCostUsd, 0);
  const activeApiKeys = apiKeys.filter(k => k.isActive).length;
  const totalGpuHours = jobs.reduce((acc, j) => acc + j.maxRuntimeHours, 0);
  const completedJobs = jobs.filter(j => j.status === 'completed').length;
  const failedJobs = jobs.filter(j => j.status === 'failed').length;

  // Helper functions
  const getInitials = (email: string | undefined) => {
    if (!email) return 'U';
    const parts = email.split('@')[0].split(/[._-]/);
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return email.substring(0, 2).toUpperCase();
  };

  const getAvatarColor = (email: string | undefined) => {
    if (!email) return 'from-surface-600 to-surface-700';
    const colors = [
      'from-blue-500 to-indigo-600',
      'from-emerald-500 to-teal-600',
      'from-violet-500 to-purple-600',
      'from-rose-500 to-pink-600',
      'from-amber-500 to-orange-600',
      'from-cyan-500 to-blue-600',
    ];
    const hash = email.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
    return colors[hash % colors.length];
  };

  const copyToClipboard = (text: string, id: string) => {
    navigator.clipboard.writeText(text);
    setCopiedText(id);
    setTimeout(() => setCopiedText(null), 2000);
  };

  // Handlers
  const handleAvatarChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Image must be less than 5MB');
        return;
      }

      // Validate file type
      if (!file.type.startsWith('image/')) {
        toast.error('Please select a valid image file');
        return;
      }

      setIsUploadingAvatar(true);

      // Show preview immediately
      const reader = new FileReader();
      reader.onloadend = () => {
        setAvatarPreview(reader.result as string);
      };
      reader.readAsDataURL(file);

      // Upload to server
      uploadAvatarMutation.mutate(file);
    }

    // Reset input so same file can be selected again
    if (e.target) {
      e.target.value = '';
    }
  };

  const handleSaveProfile = () => {
    updateProfileMutation.mutate({
      displayName: profileForm.displayName,
      company: profileForm.company,
      jobTitle: profileForm.jobTitle,
      phone: profileForm.phone,
      bio: profileForm.bio,
      timezone: profileForm.timezone,
      dateFormat: profileForm.dateFormat,
    });
  };

  const handleChangePassword = () => {
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    if (passwordForm.newPassword.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    changePasswordMutation.mutate({
      currentPassword: passwordForm.currentPassword,
      newPassword: passwordForm.newPassword,
    });
  };

  const handleEnable2FA = () => {
    init2FAMutation.mutate();
  };

  const handleConfirm2FA = () => {
    if (!twoFactorCode || twoFactorCode.length !== 6) {
      toast.error('Please enter a valid 6-digit code');
      return;
    }
    verify2FAMutation.mutate(twoFactorCode);
  };

  const handleDisable2FA = () => {
    // In production, you would show a modal to get password and code
    // For now, we'll just disable it with placeholder values
    const password = prompt('Enter your password to disable 2FA:');
    const code = prompt('Enter your 2FA code:');
    if (password && code) {
      disable2FAMutation.mutate({ password, code });
    }
  };

  const handleRevokeSession = (sessionId: string) => {
    revokeSessionMutation.mutate(sessionId);
  };

  const handleRevokeAllSessions = () => {
    revokeAllSessionsMutation.mutate();
  };

  const handleDeleteAccount = () => {
    const password = prompt('Enter your password to confirm account deletion:');
    if (password) {
      if (confirm('Are you absolutely sure? This action cannot be undone.')) {
        deleteAccountMutation.mutate(password);
      }
    }
  };

  const handleDownloadRecoveryCodes = () => {
    const password = prompt('Enter your password to regenerate and download new recovery codes:');
    if (password) {
      regenerateRecoveryCodesMutation.mutate(password);
    }
  };

  const handleSavePreferences = async () => {
    try {
      // Save notification preferences to API
      await updatePreferencesMutation.mutateAsync({
        theme: preferences.theme,
        compactMode: preferences.compactMode,
        jobCompletionAlerts: preferences.jobCompletionAlerts,
        costAlerts: preferences.costAlerts,
        securityAlerts: preferences.securityAlerts,
        productUpdates: preferences.productUpdates,
        weeklyDigest: preferences.weeklyDigest,
      });

      // Save regional settings (timezone, dateFormat) to profile API
      await updateProfileMutation.mutateAsync({
        timezone: profileForm.timezone,
        dateFormat: profileForm.dateFormat,
      });

      // Sync with local preferences store for immediate application
      preferencesStore.setTheme(preferences.theme);
      preferencesStore.setCompactMode(preferences.compactMode);
      preferencesStore.setTimezone(profileForm.timezone);
      preferencesStore.setDateFormat(profileForm.dateFormat);
    } catch {
      // Error already handled in mutations
    }
  };

  const handleExportData = () => {
    exportDataMutation.mutate();
  };

  // Notification handlers
  const handleSaveNotifications = () => {
    updatePreferencesMutation.mutate(notificationPreferences, {
      onSuccess: (data) => {
        setNotificationPreferences(data);
        setInitialNotificationPreferences(data);
        setNotificationHasChanges(false);
        queryClient.invalidateQueries({ queryKey: ['notification-preferences'] });
      },
    });
  };

  const handleNotificationToggle = (key: keyof NotificationPreferences) => {
    if (key === 'securityAlerts') {
      toast.error('Security alerts cannot be disabled for your protection');
      return;
    }
    setNotificationPreferences(prev => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const handleNotificationThresholdChange = (value: number) => {
    setNotificationPreferences(prev => ({
      ...prev,
      costAlertThreshold: value,
    }));
  };

  const handleAdditionalNotificationToggle = (key: keyof typeof additionalNotificationSettings) => {
    setAdditionalNotificationSettings(prev => ({
      ...prev,
      [key]: !prev[key],
    }));
    toast.success('Setting updated');
  };

  const requestPushPermission = async () => {
    if (!('Notification' in window)) {
      toast.error('Push notifications are not supported in this browser');
      return;
    }

    try {
      const permission = await Notification.requestPermission();
      if (permission === 'granted') {
        setAdditionalNotificationSettings(prev => ({ ...prev, pushEnabled: true }));
        toast.success('Push notifications enabled');
        new Notification('Tyaus', {
          body: 'Push notifications are now enabled!',
          icon: '/TY.png',
        });
      } else {
        toast.error('Push notification permission denied');
      }
    } catch {
      toast.error('Failed to enable push notifications');
    }
  };

  const getActivityIcon = (action: string) => {
    const iconClass = "w-4 h-4";
    if (action.includes('created') || action.includes('started')) {
      return <Cpu className={`${iconClass} text-emerald-400`} />;
    }
    if (action.includes('cancelled') || action.includes('terminated')) {
      return <XCircle className={`${iconClass} text-orange-400`} />;
    }
    if (action.includes('completed')) {
      return <CheckCircle className={`${iconClass} text-blue-400`} />;
    }
    if (action.includes('failed')) {
      return <AlertTriangle className={`${iconClass} text-red-400`} />;
    }
    if (action.includes('login')) {
      return <Lock className={`${iconClass} text-cyan-400`} />;
    }
    if (action.includes('key')) {
      return <Key className={`${iconClass} text-amber-400`} />;
    }
    return <Activity className={`${iconClass} text-surface-400`} />;
  };

  const tabs = [
    { id: 'profile' as const, label: 'Profile', icon: User },
    { id: 'security' as const, label: 'Security', icon: Shield },
    { id: 'billing' as const, label: 'Billing & Usage', icon: CreditCard },
    { id: 'activity' as const, label: 'Activity', icon: History },
    { id: 'preferences' as const, label: 'Preferences', icon: Palette },
    { id: 'notifications' as const, label: 'Notifications', icon: Bell },
  ];

  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      className="max-w-5xl mx-auto space-y-8"
    >
      {/* Header */}
      <motion.header variants={itemVariants}>
        <h1 className="text-2xl sm:text-4xl font-semibold tracking-tight text-white mb-2">
          Profile
        </h1>
        <p className="text-sm sm:text-base text-surface-400">
          Manage your account, security settings, and preferences
        </p>
      </motion.header>

      {/* Profile Card */}
      <motion.div variants={itemVariants} className="bg-surface-900/80 border border-surface-800/60 rounded-2xl overflow-hidden">
        {/* Profile Info */}
        <div className="px-8 py-8">
          <div className="flex flex-col sm:flex-row sm:items-end gap-6">
            {/* Avatar */}
            <div className="relative group">
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleAvatarChange}
                className="hidden"
              />
              {avatarPreview ? (
                <img
                  src={avatarPreview}
                  alt="Profile"
                  className="w-24 h-24 rounded-2xl object-cover shadow-xl border-4 border-surface-900"
                />
              ) : (
                <div className={clsx(
                  'w-24 h-24 rounded-2xl bg-gradient-to-br flex items-center justify-center text-white text-3xl font-bold shadow-xl border-4 border-surface-800/60',
                  getAvatarColor(user?.email)
                )}>
                  {getInitials(user?.email)}
                </div>
              )}
              <button
                onClick={() => fileInputRef.current?.click()}
                className="absolute inset-0 flex items-center justify-center bg-black/50 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity"
              >
                <Camera className="w-6 h-6 text-white" />
              </button>
              {isUploadingAvatar && (
                <div className="absolute inset-0 flex items-center justify-center bg-black/50 rounded-2xl">
                  <RefreshCw className="w-6 h-6 text-white animate-spin" />
                </div>
              )}
            </div>

            {/* User Info */}
            <div className="flex-1 pb-2">
              <div className="flex items-center gap-3 flex-wrap">
                <h2 className="text-2xl font-semibold text-white">
                  {profileForm.displayName || user?.email?.split('@')[0] || 'User'}
                </h2>
                {twoFactorEnabled && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-medium bg-emerald-500/15 text-emerald-400 border border-emerald-500/20">
                    <Shield className="w-3 h-3" />
                    2FA Enabled
                  </span>
                )}
              </div>
              <p className="text-surface-400 mt-1 flex items-center gap-2">
                <Mail className="w-4 h-4" />
                {user?.email}
              </p>
              <div className="flex items-center gap-4 mt-3 flex-wrap">
                <span className="inline-flex items-center px-3 py-1 rounded-lg text-xs font-medium bg-gpu-500/15 text-gpu-400 border border-gpu-500/20 capitalize">
                  <Shield className="w-3 h-3 mr-1.5" />
                  {user?.role || 'Member'}
                </span>
                <span className="text-sm text-surface-500 flex items-center gap-1.5">
                  <Calendar className="w-4 h-4" />
                  Joined {format(new Date(), 'MMMM yyyy')}
                </span>
                {profileForm.company && (
                  <span className="text-sm text-surface-500 flex items-center gap-1.5">
                    <Building className="w-4 h-4" />
                    {profileForm.company}
                  </span>
                )}
              </div>
            </div>

            {/* Quick Actions */}
            <div className="flex items-center gap-2">
              <button
                onClick={handleExportData}
                className="p-2.5 rounded-xl bg-surface-800/50 hover:bg-surface-800 text-surface-400 hover:text-surface-200 transition-colors"
                title="Export your data"
              >
                <Download className="w-5 h-5" />
              </button>
              <Link
                to="/api-keys"
                className="p-2.5 rounded-xl bg-surface-800/50 hover:bg-surface-800 text-surface-400 hover:text-surface-200 transition-colors"
                title="API Keys"
              >
                <Key className="w-5 h-5" />
              </Link>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Tabs */}
      <motion.div variants={itemVariants}>
        <div className="flex items-center gap-1 p-1 bg-surface-900/50 rounded-xl border border-surface-800/50 overflow-x-auto">
          {tabs.map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              onClick={() => handleTabChange(id)}
              className={clsx(
                'flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 whitespace-nowrap',
                activeTab === id
                  ? 'bg-surface-800 text-white shadow-sm'
                  : 'text-surface-400 hover:text-surface-200 hover:bg-surface-800/50'
              )}
            >
              <Icon className="w-4 h-4" />
              {label}
            </button>
          ))}
        </div>
      </motion.div>

      {/* Tab Content */}
      <AnimatePresence mode="wait">
        {/* Profile Tab */}
        {activeTab === 'profile' && (
          <motion.div
            key="profile"
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            className="space-y-6"
          >
            {/* Personal Information */}
            <div className="bg-surface-900/80 border border-surface-800/60 rounded-2xl p-6">
              <h3 className="text-lg font-semibold text-white mb-6 flex items-center gap-2">
                <User className="w-5 h-5 text-surface-400" />
                Personal Information
              </h3>
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-surface-400 mb-2">Display Name</label>
                  <input
                    type="text"
                    value={profileForm.displayName}
                    onChange={(e) => setProfileForm({ ...profileForm, displayName: e.target.value })}
                    className="input"
                    placeholder="Your display name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-surface-400 mb-2">Email Address</label>
                  <div className="relative">
                    <input
                      type="email"
                      value={profileForm.email}
                      className="input pr-20"
                      disabled
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-surface-500 bg-surface-800 px-2 py-0.5 rounded">
                      Verified
                    </span>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-surface-400 mb-2">
                    <Building className="w-4 h-4 inline mr-1" />
                    Company
                  </label>
                  <input
                    type="text"
                    value={profileForm.company}
                    onChange={(e) => setProfileForm({ ...profileForm, company: e.target.value })}
                    className="input"
                    placeholder="Your company name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-surface-400 mb-2">
                    <Briefcase className="w-4 h-4 inline mr-1" />
                    Job Title
                  </label>
                  <input
                    type="text"
                    value={profileForm.jobTitle}
                    onChange={(e) => setProfileForm({ ...profileForm, jobTitle: e.target.value })}
                    className="input"
                    placeholder="e.g. ML Engineer"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-surface-400 mb-2">
                    <Phone className="w-4 h-4 inline mr-1" />
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    value={profileForm.phone}
                    onChange={(e) => setProfileForm({ ...profileForm, phone: e.target.value })}
                    className="input"
                    placeholder="+1 (555) 000-0000"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-surface-400 mb-2">User ID</label>
                  <div className="relative">
                    <input
                      type="text"
                      value={user?.id || ''}
                      className="input pr-10 font-mono text-sm"
                      disabled
                    />
                    <button
                      onClick={() => copyToClipboard(user?.id || '', 'user-id')}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-surface-500 hover:text-surface-300"
                    >
                      {copiedText === 'user-id' ? <Check className="w-4 h-4 text-emerald-400" /> : <Copy className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
              </div>
              <div className="mt-6">
                <label className="block text-sm font-medium text-surface-400 mb-2">Bio</label>
                <textarea
                  value={profileForm.bio}
                  onChange={(e) => setProfileForm({ ...profileForm, bio: e.target.value })}
                  className="input min-h-[100px] resize-none"
                  placeholder="Tell us a bit about yourself and your work with GPU computing..."
                />
                <p className="text-xs text-surface-500 mt-1">{profileForm.bio.length}/500 characters</p>
              </div>
              <div className="mt-6 flex justify-end">
                <button
                  onClick={handleSaveProfile}
                  className="btn-primary"
                  disabled={updateProfileMutation.isPending}
                >
                  {updateProfileMutation.isPending ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Save className="w-4 h-4" />
                  )}
                  {updateProfileMutation.isPending ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </div>

            {/* Quick Links */}
            <div className="grid md:grid-cols-3 gap-4">
              <Link
                to="/api-keys"
                className="bg-surface-900/80 border border-surface-800/60 rounded-2xl p-5 hover:border-surface-700/60 transition-colors group"
              >
                <div className="w-10 h-10 rounded-xl bg-amber-500/15 flex items-center justify-center mb-4">
                  <Key className="w-5 h-5 text-amber-400" />
                </div>
                <h4 className="text-base font-semibold text-white mb-1 group-hover:text-gpu-400 transition-colors">
                  API Keys
                </h4>
                <p className="text-sm text-surface-500">
                  {activeApiKeys} active key{activeApiKeys !== 1 ? 's' : ''}
                </p>
              </Link>

              <button
                onClick={() => setActiveTab('security')}
                className="bg-surface-900/80 border border-surface-800/60 rounded-2xl p-5 hover:border-surface-700/60 transition-colors group text-left"
              >
                <div className="w-10 h-10 rounded-xl bg-emerald-500/15 flex items-center justify-center mb-4">
                  <Shield className="w-5 h-5 text-emerald-400" />
                </div>
                <h4 className="text-base font-semibold text-white mb-1 group-hover:text-gpu-400 transition-colors">
                  Security
                </h4>
                <p className="text-sm text-surface-500">
                  {twoFactorEnabled ? '2FA enabled' : 'Enable 2FA'}
                </p>
              </button>

              <button
                onClick={() => setActiveTab('billing')}
                className="bg-surface-900/80 border border-surface-800/60 rounded-2xl p-5 hover:border-surface-700/60 transition-colors group text-left"
              >
                <div className="w-10 h-10 rounded-xl bg-blue-500/15 flex items-center justify-center mb-4">
                  <CreditCard className="w-5 h-5 text-blue-400" />
                </div>
                <h4 className="text-base font-semibold text-white mb-1 group-hover:text-gpu-400 transition-colors">
                  Billing
                </h4>
                <p className="text-sm text-surface-500">
                  ${totalSpend.toFixed(2)} total spend
                </p>
              </button>
            </div>

            {/* Account Stats */}
            <div className="bg-surface-900/80 border border-surface-800/60 rounded-2xl p-6">
              <h3 className="text-lg font-semibold text-white mb-6 flex items-center gap-2">
                <BarChart3 className="w-5 h-5 text-surface-400" />
                Account Overview
              </h3>
              <div className="grid md:grid-cols-4 gap-4">
                <div className="text-center p-4 rounded-xl bg-surface-800/40 border border-surface-700/30">
                  <div className="flex items-center justify-center gap-2 mb-2">
                    <Cpu className="w-5 h-5 text-gpu-400" />
                  </div>
                  <p className="text-3xl font-semibold text-white">{totalJobs}</p>
                  <p className="text-sm text-surface-500 mt-1">Total Jobs</p>
                </div>
                <div className="text-center p-4 rounded-xl bg-surface-800/40 border border-surface-700/30">
                  <div className="flex items-center justify-center gap-2 mb-2">
                    <DollarSign className="w-5 h-5 text-amber-400" />
                  </div>
                  <p className="text-3xl font-semibold text-white">${totalSpend.toFixed(2)}</p>
                  <p className="text-sm text-surface-500 mt-1">Total Spent</p>
                </div>
                <div className="text-center p-4 rounded-xl bg-surface-800/40 border border-surface-700/30">
                  <div className="flex items-center justify-center gap-2 mb-2">
                    <Key className="w-5 h-5 text-blue-400" />
                  </div>
                  <p className="text-3xl font-semibold text-white">{activeApiKeys}</p>
                  <p className="text-sm text-surface-500 mt-1">Active Keys</p>
                </div>
                <div className="text-center p-4 rounded-xl bg-surface-800/40 border border-surface-700/30">
                  <div className="flex items-center justify-center gap-2 mb-2">
                    <Zap className="w-5 h-5 text-violet-400" />
                  </div>
                  <p className="text-3xl font-semibold text-white">{totalGpuHours}h</p>
                  <p className="text-sm text-surface-500 mt-1">GPU Hours</p>
                </div>
              </div>
            </div>
          </motion.div>
        )}

        {/* Security Tab */}
        {activeTab === 'security' && (
          <motion.div
            key="security"
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            className="space-y-6"
          >
            {/* Security Overview */}
            <section className="grid md:grid-cols-3 gap-4">
  <div
    className={clsx(
      'p-4 rounded-xl border transition-colors',
      twoFactorEnabled
        ? 'bg-emerald-500/10 border-emerald-500/30'
        : 'bg-amber-500/10 border-amber-500/30'
    )}
  >
    <div className="flex items-center gap-3">
      <div
        className={clsx(
          'w-10 h-10 rounded-xl flex items-center justify-center',
          twoFactorEnabled ? 'bg-emerald-500/20' : 'bg-amber-500/20'
        )}
      >
        <Shield
          className={clsx(
            'w-5 h-5',
            twoFactorEnabled ? 'text-emerald-400' : 'text-amber-400'
          )}
        />
      </div>
      <div>
        <p className="text-sm font-medium text-surface-200">2FA Status</p>
        <p
          className={clsx(
            'text-xs',
            twoFactorEnabled ? 'text-emerald-400' : 'text-amber-400'
          )}
        >
          {twoFactorEnabled ? 'Enabled' : 'Not Enabled'}
        </p>
      </div>
    </div>
  </div>

  <div className="p-4 rounded-xl bg-surface-800/40 border border-surface-700/30">
    <div className="flex items-center gap-3">
      <div className="w-10 h-10 rounded-xl bg-blue-500/20 flex items-center justify-center">
        <Monitor className="w-5 h-5 text-blue-400" />
      </div>
      <div>
        <p className="text-sm font-medium text-surface-200">Active Sessions</p>
        <p className="text-xs text-surface-400">
          {sessions.length} device(s)
        </p>
      </div>
    </div>
  </div>

  <div className="p-4 rounded-xl bg-surface-800/40 border border-surface-700/30">
    <div className="flex items-center gap-3">
      <div className="w-10 h-10 rounded-xl bg-violet-500/20 flex items-center justify-center">
        <Key className="w-5 h-5 text-violet-400" />
      </div>
      <div>
        <p className="text-sm font-medium text-surface-200">Account</p>
        <p className="text-xs text-surface-400">{user?.email}</p>
      </div>
    </div>
  </div>
</section>


            {/* Password Change */}
            <div className="bg-surface-900/80 border border-surface-800/60 rounded-2xl p-6">
              <h3 className="text-lg font-semibold text-white mb-6 flex items-center gap-2">
                <Lock className="w-5 h-5 text-surface-400" />
                Change Password
              </h3>
              <div className="max-w-md space-y-4">
                <div>
                  <label className="block text-sm font-medium text-surface-400 mb-2">Current Password</label>
                  <div className="relative">
                    <input
                      type={showCurrentPassword ? 'text' : 'password'}
                      value={passwordForm.currentPassword}
                      onChange={(e) => setPasswordForm({ ...passwordForm, currentPassword: e.target.value })}
                      className="input pr-10"
                      placeholder="Enter current password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-surface-500 hover:text-surface-300"
                    >
                      {showCurrentPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-surface-400 mb-2">New Password</label>
                  <div className="relative">
                    <input
                      type={showNewPassword ? 'text' : 'password'}
                      value={passwordForm.newPassword}
                      onChange={(e) => setPasswordForm({ ...passwordForm, newPassword: e.target.value })}
                      className="input pr-10"
                      placeholder="Enter new password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowNewPassword(!showNewPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-surface-500 hover:text-surface-300"
                    >
                      {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                  <p className="text-xs text-surface-500 mt-1">Minimum 8 characters with uppercase, lowercase, and numbers</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-surface-400 mb-2">Confirm New Password</label>
                  <input
                    type="password"
                    value={passwordForm.confirmPassword}
                    onChange={(e) => setPasswordForm({ ...passwordForm, confirmPassword: e.target.value })}
                    className="input"
                    placeholder="Confirm new password"
                  />
                </div>
                <button
                  onClick={handleChangePassword}
                  disabled={!passwordForm.currentPassword || !passwordForm.newPassword || !passwordForm.confirmPassword || changePasswordMutation.isPending}
                  className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {changePasswordMutation.isPending ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                      Updating...
                    </>
                  ) : (
                    'Update Password'
                  )}
                </button>
              </div>
            </div>

            {/* Two-Factor Authentication */}
            <div className="bg-surface-900/80 border border-surface-800/60 rounded-2xl p-6">
              <div className="flex items-start justify-between mb-6">
                <div>
                  <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                    <Smartphone className="w-5 h-5 text-surface-400" />
                    Two-Factor Authentication
                  </h3>
                  <p className="text-sm text-surface-500 mt-1">
                    Add an extra layer of security to your account
                  </p>
                </div>
                {twoFactorEnabled ? (
                  <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-lg text-xs font-medium bg-emerald-500/15 text-emerald-400 border border-emerald-500/25">
                    <CheckCircle className="w-3.5 h-3.5" />
                    Enabled
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-lg text-xs font-medium bg-amber-500/15 text-amber-400 border border-amber-500/25">
                    <AlertTriangle className="w-3.5 h-3.5" />
                    Not Enabled
                  </span>
                )}
              </div>

              {!twoFactorEnabled && !showTwoFactorSetup && (
                <div className="p-4 rounded-xl bg-surface-800/40 border border-surface-700/30">
                  <p className="text-sm text-surface-400 mb-4">
                    Two-factor authentication adds an additional layer of security to your account by requiring a code from your authenticator app when signing in.
                  </p>
                  <button
                    onClick={handleEnable2FA}
                    className="btn-primary"
                    disabled={init2FAMutation.isPending}
                  >
                    {init2FAMutation.isPending ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Shield className="w-4 h-4" />
                    )}
                    {init2FAMutation.isPending ? 'Setting up...' : 'Enable 2FA'}
                  </button>
                </div>
              )}

              {showTwoFactorSetup && twoFactorSetupData && (
                <div className="p-4 rounded-xl bg-surface-800/40 border border-surface-700/30">
                  <div className="flex flex-col md:flex-row gap-6">
                    <div className="flex-shrink-0">
                      <div className="w-40 h-40 bg-white rounded-xl p-2 mx-auto">
                        {/* QR Code - using the otpauth URL */}
                        <div className="w-full h-full bg-gradient-to-br from-surface-200 to-surface-400 rounded-lg flex items-center justify-center">
                          <span className="text-surface-600 text-xs text-center px-2">
                            Scan with<br />authenticator app<br />or use manual key
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="flex-1">
                      <h4 className="text-sm font-medium text-white mb-2">Setup Instructions</h4>
                      <ol className="text-sm text-surface-400 space-y-2 list-decimal list-inside">
                        <li>Install an authenticator app (Google Authenticator, Authy, etc.)</li>
                        <li>Add a new account using the manual key below</li>
                        <li>Enter the 6-digit code from your app below</li>
                      </ol>
                      <div className="mt-4">
                        <label className="block text-sm font-medium text-surface-400 mb-2">Verification Code</label>
                        <div className="flex gap-2">
                          <input
                            type="text"
                            className="input w-40 font-mono tracking-widest text-center"
                            placeholder="000000"
                            maxLength={6}
                            value={twoFactorCode}
                            onChange={(e) => setTwoFactorCode(e.target.value.replace(/\D/g, ''))}
                          />
                          <button
                            onClick={handleConfirm2FA}
                            className="btn-primary"
                            disabled={verify2FAMutation.isPending || twoFactorCode.length !== 6}
                          >
                            {verify2FAMutation.isPending ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : null}
                            {verify2FAMutation.isPending ? 'Verifying...' : 'Verify & Enable'}
                          </button>
                        </div>
                      </div>
                      <div className="mt-4">
                        <p className="text-xs text-surface-500">Manual entry key:</p>
                        <code className="text-xs text-gpu-400 font-mono bg-surface-800 px-2 py-1 rounded mt-1 inline-block select-all">
                          {twoFactorSetupData.secret}
                        </code>
                      </div>
                    </div>
                  </div>
                  <div className="mt-4 flex gap-2">
                    <button
                      onClick={() => {
                        setShowTwoFactorSetup(false);
                        setTwoFactorSetupData(null);
                        setTwoFactorCode('');
                      }}
                      className="btn-secondary text-sm"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}

              {twoFactorEnabled && (
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-4 rounded-xl bg-emerald-500/10 border border-emerald-500/20">
                    <div className="flex items-center gap-3">
                      <CheckCircle className="w-5 h-5 text-emerald-400" />
                      <div>
                        <p className="text-sm font-medium text-surface-200">Authenticator App</p>
                        <p className="text-xs text-surface-500">Added on {format(new Date(), 'MMM d, yyyy')}</p>
                      </div>
                    </div>
                    <button onClick={handleDisable2FA} className="text-sm text-red-400 hover:text-red-300">
                      Disable
                    </button>
                  </div>
                  <div className="p-4 rounded-xl bg-surface-800/40 border border-surface-700/30">
                    <h4 className="text-sm font-medium text-white mb-2">Recovery Codes</h4>
                    <p className="text-sm text-surface-400 mb-3">
                      Save these codes in a secure place. You can use them to access your account if you lose your device.
                    </p>
                    <button
                      onClick={handleDownloadRecoveryCodes}
                      disabled={regenerateRecoveryCodesMutation.isPending}
                      className="btn-secondary text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {regenerateRecoveryCodesMutation.isPending ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Download className="w-4 h-4" />
                      )}
                      {regenerateRecoveryCodesMutation.isPending ? 'Generating...' : 'Download Recovery Codes'}
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Active Sessions */}
            <div className="bg-surface-900/80 border border-surface-800/60 rounded-2xl p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                  <Monitor className="w-5 h-5 text-surface-400" />
                  Active Sessions
                </h3>
                <button
                  onClick={handleRevokeAllSessions}
                  disabled={revokeAllSessionsMutation.isPending}
                  className="text-sm text-red-400 hover:text-red-300 flex items-center gap-1 disabled:opacity-50"
                >
                  {revokeAllSessionsMutation.isPending ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <LogOut className="w-4 h-4" />
                  )}
                  Sign out all other sessions
                </button>
              </div>
              {sessionsLoading ? (
                <div className="flex items-center justify-center py-12">
                  <RefreshCw className="w-6 h-6 text-surface-400 animate-spin" />
                </div>
              ) : sessions.length === 0 ? (
                <div className="text-center py-8">
                  <Monitor className="w-10 h-10 text-surface-600 mx-auto mb-3" />
                  <p className="text-surface-400">No active sessions</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {sessions.map((session: UserSession) => (
                    <div
                      key={session.id}
                      className={clsx(
                        'flex items-center justify-between p-4 rounded-xl border transition-colors',
                        session.isCurrent
                          ? 'bg-gpu-500/10 border-gpu-500/30'
                          : 'bg-surface-800/40 border-surface-700/30 hover:border-surface-600/50'
                      )}
                    >
                      <div className="flex items-center gap-4">
                        <div className={clsx(
                          'w-10 h-10 rounded-xl flex items-center justify-center',
                          session.isCurrent ? 'bg-gpu-500/20' : 'bg-surface-700/50'
                        )}>
                          <Monitor className={clsx('w-5 h-5', session.isCurrent ? 'text-gpu-400' : 'text-surface-400')} />
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <p className="text-sm font-medium text-surface-200">{session.os || session.device}</p>
                            <span className="text-xs text-surface-500">•</span>
                            <p className="text-sm text-surface-400">{session.browser}</p>
                            {session.isCurrent && (
                              <span className="px-1.5 py-0.5 rounded text-xs font-medium bg-gpu-500/20 text-gpu-400">
                                Current
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-2 mt-1">
                            <MapPin className="w-3 h-3 text-surface-500" />
                            <span className="text-xs text-surface-500">{session.location || 'Unknown location'}</span>
                            <span className="text-xs text-surface-600">•</span>
                            <span className="text-xs text-surface-500">{session.ip}</span>
                            <span className="text-xs text-surface-600">•</span>
                            <span className="text-xs text-surface-500">
                              {session.isCurrent ? 'Active now' : formatDistanceToNow(new Date(session.lastActive), { addSuffix: true })}
                            </span>
                          </div>
                        </div>
                      </div>
                      {!session.isCurrent && (
                        <button
                          onClick={() => handleRevokeSession(session.id)}
                          disabled={revokeSessionMutation.isPending}
                          className="p-2 rounded-lg text-surface-500 hover:text-red-400 hover:bg-red-500/10 transition-colors disabled:opacity-50"
                        >
                          {revokeSessionMutation.isPending ? (
                            <Loader2 className="w-5 h-5 animate-spin" />
                          ) : (
                            <XCircle className="w-5 h-5" />
                          )}
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Login History */}
            <div className="bg-surface-900/80 border border-surface-800/60 rounded-2xl p-6">
              <h3 className="text-lg font-semibold text-white mb-6 flex items-center gap-2">
                <History className="w-5 h-5 text-surface-400" />
                Recent Login Activity
              </h3>
              {loginHistoryLoading ? (
                <div className="flex items-center justify-center py-12">
                  <RefreshCw className="w-6 h-6 text-surface-400 animate-spin" />
                </div>
              ) : loginHistory.length === 0 ? (
                <div className="text-center py-8">
                  <History className="w-10 h-10 text-surface-600 mx-auto mb-3" />
                  <p className="text-surface-400">No login history available</p>
                </div>
              ) : (
                <>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-surface-800/40">
                          <th className="text-left py-3 px-4 text-xs font-semibold text-surface-400 uppercase tracking-wider">Time</th>
                          <th className="text-left py-3 px-4 text-xs font-semibold text-surface-400 uppercase tracking-wider">Device</th>
                          <th className="text-left py-3 px-4 text-xs font-semibold text-surface-400 uppercase tracking-wider">Location</th>
                          <th className="text-left py-3 px-4 text-xs font-semibold text-surface-400 uppercase tracking-wider">IP Address</th>
                          <th className="text-left py-3 px-4 text-xs font-semibold text-surface-400 uppercase tracking-wider">Status</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-surface-800/30">
                        {loginHistory.map((event: LoginHistoryEntry) => (
                          <tr key={event.id} className="hover:bg-surface-800/30 transition-colors">
                            <td className="py-3 px-4 text-sm text-surface-400">
                              {format(new Date(event.timestamp), 'MMM d, yyyy HH:mm')}
                            </td>
                            <td className="py-3 px-4 text-sm text-surface-300">
                              {event.os ? `${event.os} / ${event.browser}` : event.device}
                            </td>
                            <td className="py-3 px-4 text-sm text-surface-400">{event.location || 'Unknown'}</td>
                            <td className="py-3 px-4 text-sm text-surface-500 font-mono">{event.ip}</td>
                            <td className="py-3 px-4">
                              <span className={clsx(
                                'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium',
                                event.status === 'success'
                                  ? 'bg-emerald-500/15 text-emerald-400'
                                  : 'bg-red-500/15 text-red-400'
                              )}>
                                {event.status === 'success' ? (
                                  <CheckCircle className="w-3 h-3" />
                                ) : (
                                  <XCircle className="w-3 h-3" />
                                )}
                                {event.status}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Pagination Controls */}
                  {totalLoginHistoryPages > 1 && (
                    <div className="flex items-center justify-between pt-4 mt-4 border-t border-surface-800/40">
                      <div className="text-sm text-surface-400">
                        Page {loginHistoryPage + 1} of {totalLoginHistoryPages}
                        {loginHistoryTotal > 0 && (
                          <span className="ml-2">
                            ({loginHistoryTotal} total)
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => setLoginHistoryPage(prev => Math.max(0, prev - 1))}
                          disabled={!hasPrevLoginHistoryPage}
                          className={clsx(
                            'flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium transition-colors',
                            hasPrevLoginHistoryPage
                              ? 'bg-surface-800/50 hover:bg-surface-800 text-surface-300 hover:text-white'
                              : 'bg-surface-800/20 text-surface-600 cursor-not-allowed'
                          )}
                        >
                          <ChevronLeft className="w-4 h-4" />
                          Previous
                        </button>
                        <button
                          onClick={() => setLoginHistoryPage(prev => prev + 1)}
                          disabled={!hasNextLoginHistoryPage}
                          className={clsx(
                            'flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium transition-colors',
                            hasNextLoginHistoryPage
                              ? 'bg-surface-800/50 hover:bg-surface-800 text-surface-300 hover:text-white'
                              : 'bg-surface-800/20 text-surface-600 cursor-not-allowed'
                          )}
                        >
                          Next
                          <ChevronRight className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </motion.div>
        )}

        {/* Billing & Usage Tab */}
        {activeTab === 'billing' && (
          <motion.div
            key="billing"
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            className="space-y-6"
          >
            {/* Usage Summary */}
            <div className="bg-surface-900/80 border border-surface-800/60 rounded-2xl p-6">
              <h3 className="text-lg font-semibold text-white mb-6 flex items-center gap-2">
                <PieChart className="w-5 h-5 text-surface-400" />
                Usage Summary
              </h3>
              <div className="grid md:grid-cols-4 gap-4">
                <div className="p-4 rounded-xl bg-gradient-to-br from-gpu-500/10 to-gpu-600/5 border border-gpu-500/20">
                  <div className="flex items-center gap-2 mb-2">
                    <DollarSign className="w-5 h-5 text-gpu-400" />
                    <span className="text-sm text-surface-400">Total Spend</span>
                  </div>
                  <p className="text-2xl font-semibold text-white">${totalSpend.toFixed(2)}</p>
                  <p className="text-xs text-surface-500 mt-1">All time</p>
                </div>
                <div className="p-4 rounded-xl bg-gradient-to-br from-blue-500/10 to-blue-600/5 border border-blue-500/20">
                  <div className="flex items-center gap-2 mb-2">
                    <Zap className="w-5 h-5 text-blue-400" />
                    <span className="text-sm text-surface-400">GPU Hours</span>
                  </div>
                  <p className="text-2xl font-semibold text-white">{totalGpuHours}h</p>
                  <p className="text-xs text-surface-500 mt-1">Total compute time</p>
                </div>
                <div className="p-4 rounded-xl bg-gradient-to-br from-emerald-500/10 to-emerald-600/5 border border-emerald-500/20">
                  <div className="flex items-center gap-2 mb-2">
                    <CheckCircle className="w-5 h-5 text-emerald-400" />
                    <span className="text-sm text-surface-400">Completed</span>
                  </div>
                  <p className="text-2xl font-semibold text-white">{completedJobs}</p>
                  <p className="text-xs text-surface-500 mt-1">Successful jobs</p>
                </div>
                <div className="p-4 rounded-xl bg-gradient-to-br from-red-500/10 to-red-600/5 border border-red-500/20">
                  <div className="flex items-center gap-2 mb-2">
                    <XCircle className="w-5 h-5 text-red-400" />
                    <span className="text-sm text-surface-400">Failed</span>
                  </div>
                  <p className="text-2xl font-semibold text-white">{failedJobs}</p>
                  <p className="text-xs text-surface-500 mt-1">Failed jobs</p>
                </div>
              </div>
            </div>

            {/* Spending Breakdown */}
            <div className="bg-surface-900/80 border border-surface-800/60 rounded-2xl p-6">
              <h3 className="text-lg font-semibold text-white mb-6 flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-surface-400" />
                Spending by Instance Type
              </h3>
              <div className="space-y-4">
                {/* Aggregate spending by instance type */}
                {Object.entries(
                  jobs.reduce((acc, job) => {
                    acc[job.instanceType] = (acc[job.instanceType] || 0) + job.estimatedCostUsd;
                    return acc;
                  }, {} as Record<string, number>)
                ).sort((a, b) => b[1] - a[1]).slice(0, 5).map(([type, amount]) => {
                  const percentage = totalSpend > 0 ? (amount / totalSpend) * 100 : 0;
                  return (
                    <div key={type} className="space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-mono text-surface-300">{type}</span>
                        <span className="text-sm text-surface-400">
                          ${amount.toFixed(2)} ({percentage.toFixed(1)}%)
                        </span>
                      </div>
                      <div className="w-full h-2 bg-surface-800 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-gradient-to-r from-gpu-500 to-teal-500 rounded-full transition-all duration-500"
                          style={{ width: `${percentage}%` }}
                        />
                      </div>
                    </div>
                  );
                })}
                {jobs.length === 0 && (
                  <div className="text-center py-8">
                    <p className="text-surface-500">No spending data yet</p>
                  </div>
                )}
              </div>
            </div>

            {/* Recent Transactions */}
            <div className="bg-surface-900/80 border border-surface-800/60 rounded-2xl p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                  <FileText className="w-5 h-5 text-surface-400" />
                  Recent Jobs & Costs
                </h3>
                <Link
                  to="/jobs"
                  className="text-sm text-surface-400 hover:text-surface-200 flex items-center gap-1"
                >
                  View all jobs
                  <ExternalLink className="w-3 h-3" />
                </Link>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-surface-800/40">
                      <th className="text-left py-3 px-4 text-xs font-semibold text-surface-400 uppercase tracking-wider">Date</th>
                      <th className="text-left py-3 px-4 text-xs font-semibold text-surface-400 uppercase tracking-wider">Job ID</th>
                      <th className="text-left py-3 px-4 text-xs font-semibold text-surface-400 uppercase tracking-wider">Instance</th>
                      <th className="text-left py-3 px-4 text-xs font-semibold text-surface-400 uppercase tracking-wider">Duration</th>
                      <th className="text-right py-3 px-4 text-xs font-semibold text-surface-400 uppercase tracking-wider">Cost</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-surface-800/30">
                    {jobs.slice(0, 10).map((job) => (
                      <tr key={job.id} className="hover:bg-surface-800/30 transition-colors">
                        <td className="py-3 px-4 text-sm text-surface-400">
                          {format(new Date(job.createdAt), 'MMM d, yyyy')}
                        </td>
                        <td className="py-3 px-4">
                          <Link
                            to={`/jobs/${job.id}`}
                            className="text-sm font-mono text-gpu-400 hover:text-gpu-300"
                          >
                            {job.id.slice(0, 8)}...
                          </Link>
                        </td>
                        <td className="py-3 px-4 text-sm font-mono text-surface-300">{job.instanceType}</td>
                        <td className="py-3 px-4 text-sm text-surface-400">{job.maxRuntimeHours}h</td>
                        <td className="py-3 px-4 text-sm text-right text-surface-200 font-medium">
                          ${job.estimatedCostUsd.toFixed(2)}
                        </td>
                      </tr>
                    ))}
                    {jobs.length === 0 && (
                      <tr>
                        <td colSpan={5} className="py-8 text-center text-surface-500">
                          No jobs yet
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Payment Method & Billing Settings */}
            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-surface-900/80 border border-surface-800/60 rounded-2xl p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                    <CreditCard className="w-5 h-5 text-surface-400" />
                    Payment Method
                  </h3>
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-medium bg-amber-500/15 text-amber-400 border border-amber-500/20">
                    <AlertTriangle className="w-3 h-3" />
                    Disabled
                  </span>
                </div>
                <div className="p-4 rounded-xl bg-surface-800/40 border border-surface-700/30 opacity-50">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-8 bg-gradient-to-r from-surface-600 to-surface-500 rounded flex items-center justify-center">
                      <CreditCard className="w-5 h-5 text-surface-400" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-surface-400">No payment method configured</p>
                      <p className="text-xs text-surface-500">Payment integration coming soon</p>
                    </div>
                  </div>
                </div>
                <button disabled className="mt-4 w-full btn-secondary text-sm opacity-50 cursor-not-allowed">
                  Add Payment Method
                </button>
              </div>

              <div className="bg-surface-900/80 border border-surface-800/60 rounded-2xl p-6">
                <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                  <Bell className="w-5 h-5 text-surface-400" />
                  Cost Alerts
                </h3>
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-3 rounded-xl bg-surface-800/40">
                    <div>
                      <p className="text-sm font-medium text-surface-200">Monthly spending alert</p>
                      <p className="text-xs text-surface-500">Alert at $100/month</p>
                    </div>
                    <ToggleSwitch
                      enabled={preferences.costAlerts}
                      onChange={(v) => setPreferences({ ...preferences, costAlerts: v })}
                    />
                  </div>
                  <div className="flex items-center justify-between p-3 rounded-xl bg-surface-800/40">
                    <div className="flex items-center gap-2">
                      <div>
                        <p className="text-sm font-medium text-surface-200">Per-job cost limit</p>
                        <p className="text-xs text-surface-500">Warn when job exceeds $50</p>
                      </div>
                      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-medium bg-amber-500/15 text-amber-400">
                        Disabled
                      </span>
                    </div>
                    <ToggleSwitch
                      enabled={false}
                      onChange={() => { }}
                      disabled={true}
                    />
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        )}

        {/* Activity Tab */}
        {activeTab === 'activity' && (
          <motion.div
            key="activity"
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            className="space-y-6"
          >
            <div className="bg-surface-900/80 border border-surface-800/60 rounded-2xl p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                  <Activity className="w-5 h-5 text-surface-400" />
                  Activity Log
                </h3>
                <button
                  onClick={handleExportData}
                  disabled={exportDataMutation.isPending}
                  className="text-sm text-surface-400 hover:text-surface-200 flex items-center gap-1 disabled:opacity-50"
                >
                  {exportDataMutation.isPending ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Download className="w-4 h-4" />
                  )}
                  Export
                </button>
              </div>

              {activityLoading ? (
                <div className="flex items-center justify-center py-12">
                  <RefreshCw className="w-6 h-6 text-surface-400 animate-spin" />
                </div>
              ) : activityLogs.length === 0 ? (
                <div className="text-center py-12">
                  <Activity className="w-12 h-12 text-surface-600 mx-auto mb-4" />
                  <p className="text-surface-400">No activity yet</p>
                  <p className="text-sm text-surface-500 mt-1">Your activity will appear here</p>
                </div>
              ) : (
                <>
                  <div className="space-y-4">
                    {activityLogs.map((activity: ActivityLog) => (
                      <div
                        key={activity.id}
                        className="flex items-start gap-4 p-4 rounded-xl bg-surface-800/40 hover:bg-surface-800/60 transition-colors"
                      >
                        <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-surface-700/50 flex items-center justify-center">
                          {getActivityIcon(activity.action)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-4">
                            <div>
                              <p className="text-sm font-medium text-surface-200">
                                {activity.action.split('.').map(p => p.charAt(0).toUpperCase() + p.slice(1)).join(' ')}
                              </p>
                              <div className="flex items-center gap-2 mt-1 flex-wrap">
                                {activity.metadata?.jobId != null && (
                                  <Link
                                    to={`/jobs/${String(activity.metadata.jobId)}`}
                                    className="text-xs font-mono text-gpu-400 hover:text-gpu-300"
                                  >
                                    Job: {String(activity.metadata.jobId).slice(0, 8)}...
                                  </Link>
                                )}
                                {activity.metadata?.instanceType != null && (
                                  <span className="text-xs text-surface-500 font-mono">
                                    {String(activity.metadata.instanceType)}
                                  </span>
                                )}
                                {activity.ipAddress && (
                                  <span className="text-xs text-surface-500">
                                    IP: {activity.ipAddress}
                                  </span>
                                )}
                              </div>
                            </div>
                            <span className="text-xs text-surface-500 whitespace-nowrap">
                              {formatDistanceToNow(new Date(activity.timestamp), { addSuffix: true })}
                            </span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Pagination Controls */}
                  {totalActivityPages > 1 && (
                    <div className="flex items-center justify-between pt-4 border-t border-surface-800/40">
                      <div className="text-sm text-surface-400">
                        Page {activityPage + 1} of {totalActivityPages}
                        {activityPagination && (
                          <span className="ml-2">
                            ({activityPagination.total} total)
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => setActivityPage(prev => Math.max(0, prev - 1))}
                          disabled={!hasPrevPage}
                          className={clsx(
                            'flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium transition-colors',
                            hasPrevPage
                              ? 'bg-surface-800/50 hover:bg-surface-800 text-surface-300 hover:text-white'
                              : 'bg-surface-800/20 text-surface-600 cursor-not-allowed'
                          )}
                        >
                          <ChevronLeft className="w-4 h-4" />
                          Previous
                        </button>
                        <button
                          onClick={() => setActivityPage(prev => prev + 1)}
                          disabled={!hasNextPage}
                          className={clsx(
                            'flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium transition-colors',
                            hasNextPage
                              ? 'bg-surface-800/50 hover:bg-surface-800 text-surface-300 hover:text-white'
                              : 'bg-surface-800/20 text-surface-600 cursor-not-allowed'
                          )}
                        >
                          Next
                          <ChevronRight className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </motion.div>
        )}

        {/* Preferences Tab */}
        {activeTab === 'preferences' && (
          <motion.div
            key="preferences"
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            className="space-y-6"
          >
            {/* Appearance */}
            <div className="bg-surface-900/80 border border-surface-800/60 rounded-2xl p-6">
              <h3 className="text-lg font-semibold text-white mb-6 flex items-center gap-2">
                <Palette className="w-5 h-5 text-surface-400" />
                Appearance
              </h3>
              <div className="space-y-4">
                <div className="flex items-center justify-between p-4 rounded-xl bg-surface-800/40">
                  <div>
                    <p className="text-sm font-medium text-surface-200">Theme</p>
                    <p className="text-xs text-surface-500 mt-0.5">Choose your preferred color scheme</p>
                  </div>
                  <select
                    value={preferences.theme}
                    onChange={(e) => {
                      const newTheme = e.target.value as 'dark' | 'light' | 'system';
                      setPreferences({ ...preferences, theme: newTheme });
                      // Apply theme immediately for instant feedback
                      preferencesStore.setTheme(newTheme);
                    }}
                    className="input w-auto min-w-[150px]"
                  >
                    <option value="dark">Dark</option>
                    <option value="light">Light</option>
                    <option value="system">System</option>
                  </select>
                </div>
                <div className="flex items-center justify-between p-4 rounded-xl bg-surface-800/40">
                  <div>
                    <p className="text-sm font-medium text-surface-200">Compact Mode</p>
                    <p className="text-xs text-surface-500 mt-0.5">Reduce spacing for more content</p>
                  </div>
                  <ToggleSwitch
                    enabled={preferences.compactMode}
                    onChange={(v) => {
                      setPreferences({ ...preferences, compactMode: v });
                      // Apply compact mode immediately for instant feedback
                      preferencesStore.setCompactMode(v);
                    }}
                  />
                </div>
              </div>
            </div>

            {/* Regional Settings */}
            <div className="bg-surface-900/80 border border-surface-800/60 rounded-2xl p-6">
              <h3 className="text-lg font-semibold text-white mb-6 flex items-center gap-2">
                <Globe className="w-5 h-5 text-surface-400" />
                Regional Settings
              </h3>
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-surface-400 mb-2">Timezone</label>
                  <select
                    value={profileForm.timezone}
                    onChange={(e) => {
                      const newTimezone = e.target.value as Timezone;
                      setProfileForm({ ...profileForm, timezone: newTimezone });
                      // Apply immediately to preferences store
                      preferencesStore.setTimezone(newTimezone);
                    }}
                    className="input"
                  >
                    <option value="utc">UTC (Coordinated Universal Time)</option>
                    <option value="est">Eastern Time (EST/EDT)</option>
                    <option value="pst">Pacific Time (PST/PDT)</option>
                    <option value="cet">Central European Time (CET/CEST)</option>
                    <option value="jst">Japan Standard Time (JST)</option>
                    <option value="ist">India Standard Time (IST)</option>
                  </select>
                  <p className="text-xs text-surface-500 mt-1.5">Used for displaying dates and scheduling jobs</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-surface-400 mb-2">Date Format</label>
                  <select
                    value={profileForm.dateFormat}
                    onChange={(e) => {
                      const newFormat = e.target.value as DateFormat;
                      setProfileForm({ ...profileForm, dateFormat: newFormat });
                      // Apply immediately to preferences store
                      preferencesStore.setDateFormat(newFormat);
                    }}
                    className="input"
                  >
                    <option value="mdy">MM/DD/YYYY (01/13/2026)</option>
                    <option value="dmy">DD/MM/YYYY (13/01/2026)</option>
                    <option value="ymd">YYYY-MM-DD (2026-01-13)</option>
                  </select>
                  <p className="text-xs text-surface-500 mt-1.5">How dates are displayed throughout the app</p>
                </div>
              </div>
            </div>

            {/* Email Notifications */}
            <div className="bg-surface-900/80 border border-surface-800/60 rounded-2xl p-6">
              <h3 className="text-lg font-semibold text-white mb-6 flex items-center gap-2">
                <Bell className="w-5 h-5 text-surface-400" />
                Email Notifications
              </h3>
              <div className="space-y-3">
                {[
                  { key: 'jobCompletionAlerts' as const, label: 'Job Completion Alerts', desc: 'Get notified when GPU jobs finish' },
                  { key: 'costAlerts' as const, label: 'Cost Alerts', desc: 'Notify when spending exceeds threshold' },
                  { key: 'securityAlerts' as const, label: 'Security Alerts', desc: 'Important security notifications (always on)' },
                  { key: 'productUpdates' as const, label: 'Product Updates', desc: 'New features and improvements' },
                  { key: 'weeklyDigest' as const, label: 'Weekly Digest', desc: 'Summary of your weekly usage and costs' },
                ].map((item) => (
                  <div key={item.key} className="flex items-center justify-between p-4 rounded-xl bg-surface-800/40">
                    <div>
                      <p className="text-sm font-medium text-surface-200">{item.label}</p>
                      <p className="text-xs text-surface-500 mt-0.5">{item.desc}</p>
                    </div>
                    <ToggleSwitch
                      enabled={preferences[item.key]}
                      onChange={(v) => setPreferences({ ...preferences, [item.key]: v })}
                      disabled={item.key === 'securityAlerts'}
                    />
                  </div>
                ))}
              </div>
            </div>

            {/* Data & Privacy */}
            <div className="bg-surface-900/80 border border-surface-800/60 rounded-2xl p-6">
              <h3 className="text-lg font-semibold text-white mb-6 flex items-center gap-2">
                <Shield className="w-5 h-5 text-surface-400" />
                Data & Privacy
              </h3>
              <div className="space-y-4">
                <div className="flex items-center justify-between p-4 rounded-xl bg-surface-800/40">
                  <div>
                    <p className="text-sm font-medium text-surface-200">Export Your Data</p>
                    <p className="text-xs text-surface-500 mt-0.5">Download a copy of all your data</p>
                  </div>
                  <button
                    onClick={handleExportData}
                    className="btn-secondary text-sm"
                    disabled={exportDataMutation.isPending}
                  >
                    {exportDataMutation.isPending ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Download className="w-4 h-4" />
                    )}
                    {exportDataMutation.isPending ? 'Exporting...' : 'Export'}
                  </button>
                </div>
              </div>
            </div>

            {/* Danger Zone */}
            <div className="bg-red-950/20 border border-red-900/30 rounded-2xl p-6">
              <h3 className="text-lg font-semibold text-red-400 mb-6 flex items-center gap-2">
                <AlertTriangle className="w-5 h-5" />
                Danger Zone
              </h3>
              <div className="flex items-center justify-between p-4 rounded-xl bg-surface-900/50 border border-red-900/20">
                <div>
                  <p className="text-sm font-medium text-surface-200">Delete Account</p>
                  <p className="text-xs text-surface-500 mt-0.5">Permanently delete your account and all data</p>
                </div>
                <button
                  onClick={handleDeleteAccount}
                  disabled={deleteAccountMutation.isPending}
                  className="px-4 py-2 text-sm font-medium text-red-400 bg-red-500/10 hover:bg-red-500/20 border border-red-500/30 rounded-lg transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {deleteAccountMutation.isPending ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Trash2 className="w-4 h-4" />
                  )}
                  {deleteAccountMutation.isPending ? 'Deleting...' : 'Delete Account'}
                </button>
              </div>
            </div>

            <div className="flex justify-end">
              <button
                onClick={handleSavePreferences}
                className="btn-primary"
                disabled={updatePreferencesMutation.isPending}
              >
                {updatePreferencesMutation.isPending ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Save className="w-4 h-4" />
                )}
                {updatePreferencesMutation.isPending ? 'Saving...' : 'Save Preferences'}
              </button>
            </div>
          </motion.div>
        )}

        {/* Notifications Tab */}
        {activeTab === 'notifications' && (
          <motion.div
            key="notifications"
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            className="space-y-6"
          >
            {/* Header with Save Button */}
            <div className="flex items-start justify-between">
              <div>
                <h2 className="text-2xl font-semibold text-white mb-2">Notifications</h2>
                <p className="text-base text-surface-400">
                  Control how and when you receive notifications
                </p>
              </div>
              {notificationHasChanges && (
                <button
                  onClick={handleSaveNotifications}
                  disabled={updatePreferencesMutation.isPending}
                  className="btn-primary"
                >
                  {updatePreferencesMutation.isPending ? (
                    <>
                      <RefreshCw className="w-4 h-4 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4" />
                      Save Changes
                    </>
                  )}
                </button>
              )}
            </div>

            {/* Notification Channels */}
            <div className="bg-surface-900/80 border border-surface-800/60 rounded-2xl p-6">
              <h3 className="text-lg font-semibold text-white mb-6 flex items-center gap-2">
                <Settings className="w-5 h-5 text-surface-400" />
                Notification Channels
              </h3>
              <div className="space-y-4">
                {/* Email */}
                <div className="flex items-center justify-between p-4 rounded-xl bg-surface-800/40 border border-surface-700/30">
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-xl bg-blue-500/20 flex items-center justify-center">
                      <Mail className="w-5 h-5 text-blue-400" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-surface-200">Email Notifications</p>
                      <p className="text-xs text-surface-500">Receive alerts via email</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <select
                      value={additionalNotificationSettings.emailDigestFrequency}
                      onChange={(e) => setAdditionalNotificationSettings(prev => ({
                        ...prev,
                        emailDigestFrequency: e.target.value as 'daily' | 'weekly' | 'monthly'
                      }))}
                      className="input w-auto text-sm"
                      disabled={!additionalNotificationSettings.emailEnabled}
                    >
                      <option value="daily">Daily Digest</option>
                      <option value="weekly">Weekly Digest</option>
                      <option value="monthly">Monthly Digest</option>
                    </select>
                    <ToggleSwitch
                      enabled={additionalNotificationSettings.emailEnabled}
                      onChange={() => handleAdditionalNotificationToggle('emailEnabled')}
                    />
                  </div>
                </div>

                {/* Push */}
                <div className="flex items-center justify-between p-4 rounded-xl bg-surface-800/40 border border-surface-700/30">
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-xl bg-violet-500/20 flex items-center justify-center">
                      <Smartphone className="w-5 h-5 text-violet-400" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-surface-200">Push Notifications</p>
                      <p className="text-xs text-surface-500">Browser push notifications</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {!additionalNotificationSettings.pushEnabled && (
                      <button
                        onClick={requestPushPermission}
                        className="btn-secondary text-sm"
                      >
                        Enable
                      </button>
                    )}
                    <ToggleSwitch
                      enabled={additionalNotificationSettings.pushEnabled}
                      onChange={() => {
                        if (!additionalNotificationSettings.pushEnabled) {
                          requestPushPermission();
                        } else {
                          setAdditionalNotificationSettings(prev => ({ ...prev, pushEnabled: false }));
                          toast.success('Push notifications disabled');
                        }
                      }}
                    />
                  </div>
                </div>

                {/* Webhook */}
                <div className="p-4 rounded-xl bg-surface-800/40 border border-surface-700/30">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 rounded-xl bg-amber-500/20 flex items-center justify-center">
                        <Webhook className="w-5 h-5 text-amber-400" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-surface-200">Webhook Integration</p>
                        <p className="text-xs text-surface-500">Send notifications to your endpoint</p>
                      </div>
                    </div>
                    <ToggleSwitch
                      enabled={additionalNotificationSettings.webhookEnabled}
                      onChange={() => handleAdditionalNotificationToggle('webhookEnabled')}
                    />
                  </div>
                  {additionalNotificationSettings.webhookEnabled && (
                    <div className="mt-4">
                      <label className="block text-sm font-medium text-surface-400 mb-2">Webhook URL</label>
                      <input
                        type="url"
                        value={additionalNotificationSettings.webhookUrl}
                        onChange={(e) => setAdditionalNotificationSettings(prev => ({ ...prev, webhookUrl: e.target.value }))}
                        className="input"
                        placeholder="https://your-server.com/webhook"
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Job Alerts */}
            <div className="bg-surface-900/80 border border-surface-800/60 rounded-2xl p-6">
              <h3 className="text-lg font-semibold text-white mb-6 flex items-center gap-2">
                <Cpu className="w-5 h-5 text-surface-400" />
                GPU Job Alerts
              </h3>
              <div className="space-y-3">
                <div className="flex items-center justify-between p-4 rounded-xl bg-surface-800/40">
                  <div>
                    <p className="text-sm font-medium text-surface-200">Job Completion Alerts</p>
                    <p className="text-xs text-surface-500">Get notified when GPU jobs finish</p>
                  </div>
                  <ToggleSwitch
                    enabled={notificationPreferences.jobCompletionAlerts}
                    onChange={() => handleNotificationToggle('jobCompletionAlerts')}
                  />
                </div>

                <div className="flex items-center justify-between p-4 rounded-xl bg-surface-800/40">
                  <div>
                    <p className="text-sm font-medium text-surface-200">Job Started Alerts</p>
                    <p className="text-xs text-surface-500">Get notified when jobs begin running</p>
                  </div>
                  <ToggleSwitch
                    enabled={additionalNotificationSettings.jobStartedAlerts}
                    onChange={() => handleAdditionalNotificationToggle('jobStartedAlerts')}
                  />
                </div>

                <div className="flex items-center justify-between p-4 rounded-xl bg-surface-800/40">
                  <div>
                    <p className="text-sm font-medium text-surface-200">Job Failed Alerts</p>
                    <p className="text-xs text-surface-500">Get notified when jobs fail or error out</p>
                  </div>
                  <ToggleSwitch
                    enabled={additionalNotificationSettings.jobFailedAlerts}
                    onChange={() => handleAdditionalNotificationToggle('jobFailedAlerts')}
                  />
                </div>

                <div className="flex items-center justify-between p-4 rounded-xl bg-surface-800/40">
                  <div>
                    <p className="text-sm font-medium text-surface-200">Spot Interruption Alerts</p>
                    <p className="text-xs text-surface-500">Get notified when spot instances are interrupted</p>
                  </div>
                  <ToggleSwitch
                    enabled={additionalNotificationSettings.spotInterruptionAlerts}
                    onChange={() => handleAdditionalNotificationToggle('spotInterruptionAlerts')}
                  />
                </div>
              </div>
            </div>

            {/* Cost Alerts */}
            <div className="bg-surface-900/80 border border-surface-800/60 rounded-2xl p-6">
              <h3 className="text-lg font-semibold text-white mb-6 flex items-center gap-2">
                <DollarSign className="w-5 h-5 text-surface-400" />
                Cost Alerts
              </h3>
              <div className="space-y-4">
                <div className="flex items-center justify-between p-4 rounded-xl bg-surface-800/40">
                  <div>
                    <p className="text-sm font-medium text-surface-200">Cost Threshold Alerts</p>
                    <p className="text-xs text-surface-500">Get alerted when spending exceeds threshold</p>
                  </div>
                  <ToggleSwitch
                    enabled={notificationPreferences.costAlerts}
                    onChange={() => handleNotificationToggle('costAlerts')}
                  />
                </div>

                {notificationPreferences.costAlerts && (
                  <div className="p-4 rounded-xl bg-surface-800/40 border border-surface-700/30">
                    <label className="block text-sm font-medium text-surface-400 mb-3">
                      Monthly Spending Threshold
                    </label>
                    <div className="flex items-center gap-4">
                      <span className="text-surface-400">$</span>
                      <input
                        type="number"
                        value={notificationPreferences.costAlertThreshold || 100}
                        onChange={(e) => handleNotificationThresholdChange(parseInt(e.target.value) || 0)}
                        className="input w-32"
                        min={0}
                        step={10}
                      />
                      <span className="text-sm text-surface-500">per month</span>
                    </div>
                    <p className="text-xs text-surface-500 mt-2">
                      You'll be notified when your spending approaches this amount
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Security Alerts */}
            <div className="bg-surface-900/80 border border-surface-800/60 rounded-2xl p-6">
              <h3 className="text-lg font-semibold text-white mb-6 flex items-center gap-2">
                <Shield className="w-5 h-5 text-surface-400" />
                Security Alerts
              </h3>
              <div className="space-y-3">
                <div className="flex items-center justify-between p-4 rounded-xl bg-emerald-500/10 border border-emerald-500/20">
                  <div className="flex items-center gap-3">
                    <Check className="w-5 h-5 text-emerald-400" />
                    <div>
                      <p className="text-sm font-medium text-surface-200">Security Alerts</p>
                      <p className="text-xs text-surface-500">Login attempts, password changes, 2FA updates</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-emerald-400">Always on</span>
                    <ToggleSwitch
                      enabled={true}
                      onChange={() => toast.error('Security alerts cannot be disabled')}
                      disabled
                    />
                  </div>
                </div>

                <div className="flex items-center justify-between p-4 rounded-xl bg-surface-800/40">
                  <div>
                    <p className="text-sm font-medium text-surface-200">API Key Usage Alerts</p>
                    <p className="text-xs text-surface-500">Get notified about unusual API key activity</p>
                  </div>
                  <ToggleSwitch
                    enabled={additionalNotificationSettings.apiKeyUsageAlerts}
                    onChange={() => handleAdditionalNotificationToggle('apiKeyUsageAlerts')}
                  />
                </div>
              </div>
            </div>

            {/* Product Updates */}
            <div className="bg-surface-900/80 border border-surface-800/60 rounded-2xl p-6">
              <h3 className="text-lg font-semibold text-white mb-6 flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-surface-400" />
                Updates & Digests
              </h3>
              <div className="space-y-3">
                <div className="flex items-center justify-between p-4 rounded-xl bg-surface-800/40">
                  <div>
                    <p className="text-sm font-medium text-surface-200">Product Updates</p>
                    <p className="text-xs text-surface-500">New features, improvements, and announcements</p>
                  </div>
                  <ToggleSwitch
                    enabled={notificationPreferences.productUpdates}
                    onChange={() => handleNotificationToggle('productUpdates')}
                  />
                </div>

                <div className="flex items-center justify-between p-4 rounded-xl bg-surface-800/40">
                  <div>
                    <p className="text-sm font-medium text-surface-200">Weekly Usage Digest</p>
                    <p className="text-xs text-surface-500">Summary of your weekly GPU usage and costs</p>
                  </div>
                  <ToggleSwitch
                    enabled={notificationPreferences.weeklyDigest}
                    onChange={() => handleNotificationToggle('weeklyDigest')}
                  />
                </div>
              </div>
            </div>

            {/* Quiet Hours */}
            <div className="bg-surface-900/80 border border-surface-800/60 rounded-2xl p-6">
              <h3 className="text-lg font-semibold text-white mb-6 flex items-center gap-2">
                {additionalNotificationSettings.quietHoursEnabled ? (
                  <VolumeX className="w-5 h-5 text-surface-400" />
                ) : (
                  <Volume2 className="w-5 h-5 text-surface-400" />
                )}
                Quiet Hours
              </h3>
              <div className="space-y-4">
                <div className="flex items-center justify-between p-4 rounded-xl bg-surface-800/40">
                  <div>
                    <p className="text-sm font-medium text-surface-200">Enable Quiet Hours</p>
                    <p className="text-xs text-surface-500">Pause non-critical notifications during set hours</p>
                  </div>
                  <ToggleSwitch
                    enabled={additionalNotificationSettings.quietHoursEnabled}
                    onChange={() => handleAdditionalNotificationToggle('quietHoursEnabled')}
                  />
                </div>

                {additionalNotificationSettings.quietHoursEnabled && (
                  <div className="p-4 rounded-xl bg-surface-800/40 border border-surface-700/30">
                    <div className="flex items-center gap-4">
                      <div>
                        <label className="block text-sm font-medium text-surface-400 mb-2">Start Time</label>
                        <input
                          type="time"
                          value={additionalNotificationSettings.quietHoursStart}
                          onChange={(e) => setAdditionalNotificationSettings(prev => ({ ...prev, quietHoursStart: e.target.value }))}
                          className="input"
                        />
                      </div>
                      <span className="text-surface-500 mt-6">to</span>
                      <div>
                        <label className="block text-sm font-medium text-surface-400 mb-2">End Time</label>
                        <input
                          type="time"
                          value={additionalNotificationSettings.quietHoursEnd}
                          onChange={(e) => setAdditionalNotificationSettings(prev => ({ ...prev, quietHoursEnd: e.target.value }))}
                          className="input"
                        />
                      </div>
                    </div>
                    <div className="mt-4 p-3 rounded-lg bg-amber-500/10 border border-amber-500/20">
                      <div className="flex items-start gap-2">
                        <AlertTriangle className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
                        <p className="text-xs text-amber-300">
                          Security alerts will still be delivered during quiet hours
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}
